import { useMemo, useRef, useState } from 'react';
import { IonItem, IonLabel, IonInput, IonButton, IonIcon, IonList, IonText, IonRow, IonCol } from '@ionic/react';
import { chevronDownOutline, searchOutline } from 'ionicons/icons';
import CountryFlag from './CountryFlag';
import { countries, states, countryCodeMap } from '@rsmaids/shared/utils/locationData';
import { PortalPickerDropdown } from './PortalPickerDropdown';
import styles from './LocationPicker.module.scss';
import { useTranslation } from 'react-i18next';
import { translateDynamic } from '../i18n/dynamic';

interface Props {
  label: string;
  value: string;
  placeholder: string;
  type: 'state' | 'country';
  onChange: (value: string) => void;
  portalRef: React.RefObject<HTMLElement | null>;
  inputId?: string;
}

const LocationPicker: React.FC<Props> = ({
  label,
  value,
  placeholder,
  type,
  onChange,
  portalRef,
  inputId,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const triggerRef = useRef<HTMLIonButtonElement>(null);
  const items = type === 'country' ? countries : states;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? items.filter(i => i.toLowerCase().includes(q)) : items;
  }, [items, search]);

  const close = () => {
    setOpen(false);
    setSearch('');
  };

  const pick = (v: string) => {
    onChange(v);
    close();
  };

  return (
    <>
      <IonRow className={styles.fieldRow}>
        <IonCol size="12" className={styles.fieldLabel}>
          <IonText className={styles.fieldLabelText}>{label}</IonText>
        </IonCol>
        <IonCol size="12">
          <IonButton
            ref={triggerRef}
            id={inputId}
            fill="clear"
            className={`${styles.trigger} ${open ? styles.triggerOpen : ''}`}
            onClick={() => setOpen(o => !o)}
          >
            {type === 'country' && value && (
              <CountryFlag
                countryCode={countryCodeMap[value] || ''}
                style={{ width: 18, height: 18, marginRight: 8, flexShrink: 0 }}
              />
            )}
            <IonText
              className={styles.triggerText}
              style={{
                color: value ? 'var(--glass-card-text, var(--ion-text-color))' : 'var(--glass-card-text-secondary, var(--ion-color-medium))',
              }}
            >
              {value || placeholder}
            </IonText>
            <IonIcon icon={chevronDownOutline} className={styles.triggerCaret} />
          </IonButton>
        </IonCol>
      </IonRow>

      <PortalPickerDropdown
        isOpen={open}
        fieldRef={triggerRef as React.RefObject<HTMLElement | null>}
        portalRef={portalRef}
        close={close}
        align="left"
        matchWidth
        className={styles.dropdown}
      >
        <IonItem lines="none" className={styles.searchItem}>
          <IonIcon icon={searchOutline} slot="start" className={styles.searchIcon} />
          <IonInput
            className={styles.searchInput}
            placeholder={t('components.LocationPicker.search', { v1: translateDynamic(type === 'country' ? 'countries' : 'states') })}
            value={search}
            onIonInput={e => setSearch(e.detail.value ?? '')}
          />
        </IonItem>
        <IonList lines="none" className={styles.dropdownList}>
          {filtered.length === 0 ? (
            <IonItem lines="none" className={styles.emptyItem}>
              <IonLabel>{t('components.LocationPicker.noResults')}</IonLabel>
            </IonItem>
          ) : (
            filtered.map(item => (
              <IonItem
                key={item}
                button
                detail={false}
                lines="none"
                className={`${styles.dropdownItem} ${item === value ? styles.dropdownItemActive : ''}`}
                onClick={() => pick(item)}
              >
                {type === 'country' && (
                  <CountryFlag
                    countryCode={countryCodeMap[item] || ''}
                    style={{ width: 20, height: 20, marginRight: 12, flexShrink: 0 }}
                  />
                )}
                <IonLabel>{item}</IonLabel>
              </IonItem>
            ))
          )}
        </IonList>
      </PortalPickerDropdown>
    </>
  );
};

export default LocationPicker;
